/*
 * replaceImage — orchestrator for swapping the image on the currently-selected
 * UE node to a DAM asset path.
 *
 * Routes the write through the Universal Editor host's editorActions API
 * (https://developer.adobe.com/uix/docs/services/aem-universal-editor/api/actions/).
 * The host runs the same /patch call we'd otherwise make ourselves and then
 * pushes the rebuilt component HTML into the canvas iframe via postMessage,
 * which is what gives us the inline canvas update without a full reload.
 *
 * Flow:
 *   1. editorActions.details(editable) — fetch the component model so we can
 *      pick the right image property name (image / fileReference / ...).
 *   2. editorActions.update(target, patch) — JSON Patch (RFC 6902) `op: replace`
 *      against that property. Single field per call, replace-only — exactly
 *      what the API supports.
 */

// Property names commonly used for image references on AEM components,
// ordered by likelihood.
const PROPERTY_CANDIDATES = ['fileReference', 'image', 'src', 'imagePath', 'imageReference'];

export function findImagePropertyName(modelOrJson) {
  if (!modelOrJson || typeof modelOrJson !== 'object') return '';

  // editorActions.details returns { data, schema }. Older shapes may wrap the
  // properties under data/properties/model, or put them at the root. Search
  // common containers, then the root.
  const candidates = [
    modelOrJson.data,
    modelOrJson.properties,
    modelOrJson.model,
    modelOrJson,
  ].filter((x) => x && typeof x === 'object');

  // Pass 1: known names whose current value looks like a DAM path.
  for (const obj of candidates) {
    for (const name of PROPERTY_CANDIDATES) {
      const v = obj[name];
      if (typeof v === 'string' && v.indexOf('/content/dam/') !== -1) return name;
    }
  }
  // Pass 2: known names even if the value is empty/not-DAM.
  for (const obj of candidates) {
    for (const name of PROPERTY_CANDIDATES) {
      if (Object.prototype.hasOwnProperty.call(obj, name)) return name;
    }
  }
  // Pass 3: any string property containing /content/dam/.
  for (const obj of candidates) {
    for (const [k, v] of Object.entries(obj)) {
      if (k.startsWith('jcr:') || k.startsWith('sling:') || k.startsWith('cq:')) continue;
      if (typeof v === 'string' && v.indexOf('/content/dam/') !== -1) return k;
    }
  }
  return '';
}

/**
 * @param {object} args
 * @param {string} args.damPath  - DAM path to apply, e.g. /content/dam/wknd-universal/foo.png
 * @param {object} args.ue       - the useUEContext return value (must have
 *                                 conn and selection.{resource, editable})
 * @param {(msg:string)=>void} [args.onStatus]
 * @returns {Promise<{ok:boolean, error?:string, propertyName?:string}>}
 */
export async function replaceImage({ damPath, ue, onStatus }) {
  const say = (m) => { if (typeof onStatus === 'function') onStatus(m); };

  if (!damPath) return { ok: false, error: 'no DAM path' };
  if (!ue || !ue.conn) return { ok: false, error: 'UE not ready' };

  const editorActions = ue.conn?.host?.editorActions;
  if (!editorActions || typeof editorActions.update !== 'function') {
    return { ok: false, error: 'editorActions.update not available on this UE host' };
  }

  const editable = ue.selection?.editable;
  if (!editable) return { ok: false, error: 'nothing selected in the canvas' };

  // 1) details — discover the property name on the current component.
  // Best-effort: if the host can't resolve the editable for some reason, we
  // fall back to the most common name ('image') and let update() fail loudly.
  say('Inspecting…');
  let propertyName = '';
  if (typeof editorActions.details === 'function') {
    try {
      const details = await editorActions.details(editable);
      propertyName = findImagePropertyName(details);
      console.log('[DemoPilot] editorActions.details ok, propertyName probe:', propertyName);
    } catch (err) {
      console.warn('[DemoPilot] editorActions.details failed:', err);
    }
  }
  if (!propertyName) propertyName = 'image';

  // 2) update — JSON Patch replace on the discovered property. The host owns
  // the network round-trip + canvas postMessage, so a successful await means
  // the canvas has already been told to repaint.
  // Signature per the docs:
  //   update({ target: { editable }, patch: Operation[] })
  say('Saving…');
  try {
    await editorActions.update({
      target: { editable },
      patch: [{ op: 'replace', path: `/${propertyName}`, value: damPath }],
    });
  } catch (err) {
    console.error('[DemoPilot] editorActions.update failed:', err);
    return { ok: false, error: err?.message || 'update failed', propertyName };
  }

  say('Replaced');
  return { ok: true, propertyName };
}
